import { useEffect, useState } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table';
import '../../../pricing-simulator/components/list-price-simulation-table/list-price-simulation.scss';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { getPotentialRiskData } from '../../store/simulation-summary-slice';
import { formatNumberUsingRegex } from '../../../pricing-simulator';
import TableSkeletonContainer from '../../../pricing-simulator/components/simulation-table-container/table-skeleton';

type UpsideRowData = {
  description: string;
  nsv_change_abs: number;
  nsv_change_perc: number;
  mac_change_abs: number;
  mac_change_perc: number;
};
const columnHelper = createColumnHelper<UpsideRowData>();

export default function PotentialRiskTableComponent({
  sourceId,
  moduleId,
  scenarioId,
  disableInputs = false
}: {
  sourceId: number;
  moduleId: number;
  scenarioId: number;
  disableInputs: boolean;
}) {
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [dataInputs, setDataInputs] = useState({});
  const appDispatch = useAppDispatch();
  let dataInp = {};

  const { potentialRiskData, fetchingPotentialRiskData } = useAppSelector(
    (state) => state.simulationSummary
  );

  const { summary: summaryAccessConfig } = useAppSelector(
    (state) => state.simulatorHome.moduleAccessMap
  );

  useEffect(() => {
    if (Object.keys(potentialRiskData).length > 0) {
      //forDataInputs
      setDataInputs(potentialRiskData.inputs);
      dataInp = potentialRiskData.inputs;
      //for columns
      const cols = [];
      potentialRiskData.columns.forEach((val, index) => {
        if (index === 0) {
          cols.push(
            columnHelper.accessor(val.parent.parent_field, {
              header: val.parent.alias,
              cell: (info) => getInputText(info)
            })
          );
        } else {
          cols.push(
            columnHelper.accessor(val.parent.parent_field, {
              header: val.parent.alias,
              cell: (info) => formatNumberUsingRegex(val, info.getValue())
            })
          );
        }
      });
      setColumns(cols);

      //for data
      const rowData = [];
      potentialRiskData.rows.forEach((ele) => {
        rowData.push(ele.parent.data);
      });
      setData(rowData);
    }
  }, [potentialRiskData]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  const handleInputBlur = (e, keyValue) => {
    const dataInput = {
      filter_inputs: [],
      inputs: dataInp
    };
    appDispatch(
      getPotentialRiskData({
        sourceId,
        moduleId,
        scenarioId,
        dataInput
      })
    );
  };

  const handleInputChange = (e, keyValue) => {
    if (e.target.value !== 0 && !e.target.value) {
      return;
    }
    if (keyValue.includes('day')) {
      if (e.target.value < 0) {
        e.target.value = 0;
      }
      if ((e.target.value - Math.floor(e.target.value)) !== 0) {
        e.target.value = Math.floor(e.target.value);
      }
    }
    let obj = JSON.parse(JSON.stringify(dataInp));
    obj[keyValue] = Number(e.target.value);
    setDataInputs({ ...obj });
    dataInp = obj;
    console.log('ONCHANGE ', { ...obj }, dataInputs);
  };

  const getInputText = (info) => {
    const text = info.getValue();
    const textCopy = text.split(/\{(.+?)\}/gi);
    if (textCopy.length) {
      for (let i = 1; i < textCopy.length; i += 2) {
        const keyValue = textCopy[i];
        textCopy[i] = (
          <span className='input-box-container' key={i}>
            <input
              disabled={disableInputs || !summaryAccessConfig?.provide_inputs_potential_upside}
              className='table-row-input-box'
              type='number'
              value={dataInp[keyValue]}
              onBlur={(e) => handleInputBlur(e, keyValue)}
              onChange={(e) => handleInputChange(e, keyValue)}
            />
          </span>
        );
      }
      return <div className='input-text'>{textCopy}</div>;
    }
    return <div>{text}</div>;
  };

  return (
    <div>
      {fetchingPotentialRiskData ? (
        <TableSkeletonContainer rowLength={5} headerLength={6} />
      ) : (
        <div
          style={{ padding: '0px 10px', height: '380px' }}
          className='p-2 virtualized-table-container'
        >
          <table>
            <thead>
              {table?.getHeaderGroups().map((headerGroup) => (
                <tr className='potential-risk-header' key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table?.getRowModel().rows.map((row) => (
                <tr className='potential-risk-rows' key={row.id}>
                  {row.getVisibleCells().map((cell, index) => (
                    <td
                      style={{
                        color:
                          index == 0
                            ? 'black'
                            : cell.getValue() > 0
                              ? '#0FB052'
                              : cell.getValue() < 0
                                ? '#B80302'
                                : '#525252'
                      }}
                      key={cell.id}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {''}
          </table>
          {''}
        </div>
      )}
    </div>
  );
}
